import { useSync } from '@tldraw/sync';
import { useMemo } from 'react';
import type { TLAssetStore } from 'tldraw';
import { useAuth } from '../auth/AuthContext';
import { colorForRole } from '../lib/colors';
import { firstName } from '../lib/first-name';
import { getFreshAccessToken } from '../lib/access-token';

// Fallback asset store used when caller does not provide one.
// Stores assets as inline base64 blobs — fine for small files, not for large images.
const fallbackAssets: TLAssetStore = {
  async upload(_asset, file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ src: reader.result as string });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },
  resolve(asset) {
    return asset.props.src ?? null;
  },
};

export function useBoardSync(params: { boardId: string; assets?: TLAssetStore }) {
  const { boardId, assets } = params;
  const { user } = useAuth();

  const uri = useMemo(() => {
    return async () => {
      const token = await getFreshAccessToken();
      const wsProto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      return `${wsProto}://${window.location.host}/ws/boards/${boardId}?token=${encodeURIComponent(token)}`;
    };
  }, [boardId]);

  const userInfo = useMemo(
    () => ({
      id: user?.id ?? 'anon',
      name: firstName(user?.name),
      color: user ? colorForRole(user.role) : '#888',
    }),
    [user?.id, user?.name, user?.role],
  );

  return useSync({ uri, userInfo, assets: assets ?? fallbackAssets });
}
